import { AddIconData, RemoveIconData } from "@meiden-connect/material-symbols";
import { Button, Icon } from "@meiden-connect/ui";
import { useCanvasStore } from "../store/canvas-store";
import { stepZoomIn, stepZoomOut, zoomAt } from "../../../lib/canvas-coordinates";

// 倍率表示の押下で戻す既定倍率。ZOOM_STEPS にも 1 が含まれるため、段送りの到達点と一致する。
const ZOOM_RESET = 1;

// 「目標倍率が現在倍率と同じ」と判定する許容誤差。use-cursor-broadcaster の zoom 比較（1e-4）は
// 送信の間引き判定であり目的が異なるため、こちらは同値判定として厳しめに取る。
const ZOOM_EPSILON = 1e-6;

// ミニマップ下に配置する - / 倍率 / + ズームボタン。
// 画面中央を中心にズームを段階的に変更し、倍率表示の押下で 100% へ戻す。
export function ZoomButtons(): JSX.Element {
  const view = useCanvasStore((s) => s.view);
  const setView = useCanvasStore((s) => s.setView);
  // キャンバス面の外にあるコントロールからの view 変更は、表示位置の保存と共有 URL への反映
  // （canvas-stage の viewSyncArmed）が武装していないと反映されない。パネル上の Ctrl+ホイール
  // ズーム（use-ctrl-wheel-zoom.ts）とコメントレイヤが既にこの store アクションで武装しているため、
  // ズームボタン群も同じ経路にそろえる。
  const requestViewSyncArm = useCanvasStore((s) => s.requestViewSyncArm);

  const percent = Math.round(view.zoom * 100);

  // 画面中央はレンダー時に固定せず、押下のたびに算出する。レンダー時に固定すると、ウィンドウの
  // リサイズ後に「古い中央」を基準にズームしてしまう（view が変わらない限り再レンダーされないため
  // 値が更新されない）。
  const screenCenterNow = (): { x: number; y: number } => ({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });

  // 目標倍率が現在倍率と同値なら何もしない。同値で view を作り直すと、zoomAt の除算・乗算で pan に
  // 丸め誤差が乗り、表示が変わらないのに再レンダー・表示位置の保存・共有 URL 更新が走る
  // （applyPinchZoom が deltaY=0 を早期 return しているのと同じ理由）。
  // - / + は上下限（ZOOM_MIN / ZOOM_MAX）で stepZoom が同値を返すため、この判定が無いと端で
  // 押し続けるたびに保存と URL 更新が走ってしまう。3 つのハンドラで共有する。
  const applyZoom = (nextZoom: number): void => {
    if (Math.abs(view.zoom - nextZoom) < ZOOM_EPSILON) return;
    requestViewSyncArm();
    setView(zoomAt(view, screenCenterNow(), nextZoom));
  };

  const onZoomOut = (): void => {
    applyZoom(stepZoomOut(view.zoom));
  };
  const onZoomIn = (): void => {
    applyZoom(stepZoomIn(view.zoom));
  };
  const onZoomReset = (): void => {
    applyZoom(ZOOM_RESET);
  };

  return (
    <div className="canvas-zoom-buttons">
      <Button
        type="button"
        aria-label="縮小"
        title="縮小"
        onClick={onZoomOut}
        variant="action"
        className="canvas-icon-button"
      >
        <Icon d={RemoveIconData} />
      </Button>
      {/* 現在倍率の表示そのものを押下対象にする（倍率コントロールの一般的な作法）。
          @meiden-connect/ui の Button は font-size / font-weight / min-height を持ち込んで現在の
          文字表示の見た目を変えてしまうため、recent-canvas-card.tsx と同じ判断で素の <button> を
          使い、見た目は styles.css 側で与える。
          aria-label は要素のテキスト内容（"33%"）を上書きするため、現在値を含めないと支援技術から
          現在の倍率が読めなくなる。現在値と操作の両方を 1 つのラベルへ入れる。 */}
      <button
        type="button"
        aria-label={`倍率 ${percent}%。押すと ${ZOOM_RESET * 100}% に戻す`}
        title={`倍率を ${ZOOM_RESET * 100}% に戻す`}
        className="canvas-zoom-percent"
        data-testid="canvas-zoom-percent"
        onClick={onZoomReset}
      >
        {percent}%
      </button>
      <Button
        type="button"
        aria-label="拡大"
        title="拡大"
        onClick={onZoomIn}
        variant="action"
        className="canvas-icon-button"
      >
        <Icon d={AddIconData} />
      </Button>
    </div>
  );
}
